/*
 * /telemedicine/messages — staff dashboard for unanswered patient chat messages.
 * Shows sessions where the last message was sent by the patient with no staff reply.
 * Staff can read the full chat and respond without joining the video call.
 */
import { Container, Page, PageHeader, Placeholder } from "@emr/react-ui"
import { createFileRoute } from "@tanstack/react-router"
import { MessageSquare, Clock, User } from "lucide-react"
import { useState } from "react"
import { useAuth } from "@emr/auth/client"
import { TelemedicineChatPanel } from "../../components/telemedicine-chat-panel"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/telemedicine/messages")({
	component: TelemedicineMessagesPage,
	loader: rolePageLoader(["practitioner", "nurse", "admin", "super_admin"]),
})

function TelemedicineMessagesPage() {
	const { user } = useAuth()
	const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)

	const { data: sessions = [], isLoading } = trpc.telemedicineChat.getUnansweredSessions.useQuery(
		undefined,
		{ refetchInterval: 10000 },
	)

	return (
		<Page>
			<Container>
				<PageHeader title="Unanswered Messages" />
				<p className="text-sm text-muted-foreground mb-6">
					Sessions where a patient has sent a message with no staff reply yet.
					Respond here to notify patients of delays or answer questions without joining the video call.
				</p>

				{isLoading ? (
					<Placeholder title="Loading…" />
				) : sessions.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-20 gap-3">
						<div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
							<MessageSquare className="w-8 h-8 text-muted-foreground" />
						</div>
						<p className="text-sm font-medium text-foreground">All caught up</p>
						<p className="text-xs text-muted-foreground">No unanswered patient messages right now.</p>
					</div>
				) : (
					<div className="flex gap-4 h-[calc(100vh-240px)] min-h-[400px]">
						{/* Session list */}
						<div className="w-72 shrink-0 flex flex-col gap-2 overflow-y-auto pr-1">
							{sessions.map((s) => {
								const name = [s.patientFirstName, s.patientLastName].filter(Boolean).join(" ") || "Patient"
								const isSelected = s.id === selectedSessionId
								return (
									<button
										key={s.id}
										onClick={() => setSelectedSessionId(s.id)}
										className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
											isSelected
												? "bg-primary/10 border-primary/40"
												: "bg-card border-border hover:bg-muted"
										}`}
									>
										<div className="flex items-start gap-2">
											<div className="mt-0.5 w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
												<User className="w-3.5 h-3.5 text-muted-foreground" />
											</div>
											<div className="flex-1 min-w-0">
												<div className="flex items-center justify-between gap-1">
													<p className="text-sm font-medium truncate">{name}</p>
													<span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" title="Unanswered" />
												</div>
												{s.lastMessage && (
													<p className="text-xs text-muted-foreground truncate mt-0.5">
														{s.lastMessage.message}
													</p>
												)}
												{s.lastMessage?.createdAt && (
													<div className="flex items-center gap-1 mt-1">
														<Clock className="w-3 h-3 text-muted-foreground" />
														<span className="text-xs text-muted-foreground">
															{timeAgo(new Date(s.lastMessage.createdAt))}
														</span>
													</div>
												)}
											</div>
										</div>
									</button>
								)
							})}
						</div>

						{/* Chat panel */}
						<div className="flex-1 rounded-xl border border-border overflow-hidden bg-gray-900">
							{selectedSessionId ? (
								<TelemedicineChatPanel
									sessionId={selectedSessionId}
									myUserId={user?.id}
									className="h-full"
								/>
							) : (
								<div className="flex flex-col items-center justify-center h-full gap-3 text-center px-6">
									<MessageSquare className="w-10 h-10 text-gray-600" />
									<p className="text-sm text-gray-400">Select a session on the left to view and respond to the patient's messages.</p>
								</div>
							)}
						</div>
					</div>
				)}
			</Container>
		</Page>
	)
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function timeAgo(d: Date): string {
	const diff = Date.now() - d.getTime()
	const mins = Math.floor(diff / 60000)
	if (mins < 1) return "just now"
	if (mins < 60) return `${mins}m ago`
	const hrs = Math.floor(mins / 60)
	if (hrs < 24) return `${hrs}h ago`
	return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" })
}
